import { useEnterAnimation } from "@/pages/services/hooks/useEnterAnimation";

/**
 * AboutPhilosophy — a full-screen quiet quote panel, echoing the home
 * "quote reveal" moment but as a static, breathing pause between the
 * denser Team section and the closing CTA.
 */
export default function AboutPhilosophy() {
  const eyebrowRef = useEnterAnimation<HTMLDivElement>({ delay: 0 });
  const quoteRef = useEnterAnimation<HTMLParagraphElement>({ delay: 200 });
  const attributionRef = useEnterAnimation<HTMLDivElement>({ delay: 500 });

  return (
    <section
      id="about-philosophy"
      className="relative w-full bg-background-50 overflow-hidden"
      style={{ minHeight: "100vh" }}
    >
      {/* Section meta */}
      <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-14 md:pt-16 pointer-events-none">
        <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
            About Us / 05
          </span>
          <span className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
            Chapter 05 · A quiet belief
          </span>
        </div>
      </div>

      {/* Decorative rings */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
      >
        <div className="absolute w-[110vh] h-[110vh] rounded-full border border-foreground-950/[0.09]"></div>
        <div className="absolute w-[80vh] h-[80vh] rounded-full border border-foreground-950/[0.07]"></div>
        <div className="absolute w-[52vh] h-[52vh] rounded-full border border-foreground-950/[0.05]"></div>
      </div>

      <div className="relative z-10 w-full min-h-screen flex items-center justify-center px-6 md:px-10 py-24">
        <div className="max-w-[1100px] w-full mx-auto flex flex-col items-center text-center">
          <div ref={eyebrowRef} className="mb-12">
            <span className="inline-flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-foreground-950/70 font-medium">
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
              What we believe
              <span className="w-8 h-[1px] bg-foreground-950/35"></span>
            </span>
          </div>

          <p
            ref={quoteRef}
            className="italic mb-12 max-w-[920px]"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2rem, 4.5vw, 4.75rem)",
              fontWeight: 300,
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              color: "oklch(var(--foreground-950))",
              fontVariationSettings: "'opsz' 144",
            }}
          >
            &ldquo;You do not need to have the right words. You just need a
            place where every version of you is welcome.&rdquo;
          </p>

          <div
            ref={attributionRef}
            className="flex items-center gap-4 text-foreground-950/60"
          >
            <span className="w-10 h-[1px] bg-foreground-950/40"></span>
            <span
              className="uppercase font-medium"
              style={{ fontSize: "11px", letterSpacing: "0.3em" }}
            >
              Marta Ribeiro · Founder
            </span>
            <span className="w-10 h-[1px] bg-foreground-950/40"></span>
          </div>
        </div>
      </div>
    </section>
  );
}